import type { MenuRegel, Scherm, Toestel } from './types';

/**
 * De CCV Service app op de PAX-toestellen: A77, A920 en A35.
 *
 * ────────────────────────────────────────────────────────────────────────
 * EEN ANDER APPARAAT, NIET EEN ANDERE SKIN. De opname is gemaakt op een A77
 * (softwareversie 15.3.03.30296) die zichzelf ook als A77 meldt. De app is de
 * Android-versie: grote knoppen onder elkaar, geen grijze subteksten, en
 * infoschermen sluiten met STOP/OK in plaats van een pijl. Geen enkel menu
 * heeft bladerpijlen; één lijst (Service ▸ Rapporten ▸ Terminal ▸ Parameters,
 * 23 regels) is wél gewoon scrollbaar met een veeg.
 *
 * De menu's zelf lopen grotendeels gelijk op met de V400M, en dat is precies
 * waarom beide emulators er moeten zijn: wie de weg wijst op het verkeerde
 * beeld, wijst naar een knop die er niet zo uitziet.
 *
 * WAT ER ANDERS IS DAN OP DE V400M, kort:
 *   - boven het hoofdmenu zit een startscherm van de APP (Terminal, Menu,
 *     Pos Test, Herdruk ticket);
 *   - geen KOPIE BON / PAPER FEED in het hoofdmenu;
 *   - Ethernet in plaats van WIFI en MOBIELE NETWERKEN;
 *   - Display info heeft er C-tap, Flexo en Opi instellingen bij;
 *   - Diagnose is veel uitgebreider, en verschilt tussen manager en service;
 *   - rapporten komen als bon-tekst op het scherm ("Ticket");
 *   - het hele SERVICE MENU is een ander menu dan onder MANAGER: Rapporten,
 *     Functies en Configureren hebben er andere items onder.
 *
 * EEN LEEG RAPPORT ZIET ERUIT ALS EEN KRUIMELPAD. Levert een rapport niets op,
 * dan toont de app een Ticket-scherm met alleen het menupad in hoofdletters —
 * "TMS SESSIES / LAATSTE / LAATSTE GELUKT". Dat is geen storing en ook geen
 * half geladen scherm; het is hoe de app "geen gegevens" zegt. Op die schermen
 * negeert de terugpijl linksboven bovendien de invoer: daar werkt alleen de
 * systeemterugknop onderaan. Blijf je daar heen en weer navigeren, dan komt de
 * app met "Response timeout occurred" en blijft hij in een stapel
 * Ticket-schermen hangen; afsluiten via recents en opnieuw starten helpt. Die
 * foutdialoog staat hier niet als scherm — hij hoort bij geen enkel menu.
 *
 * GEEN WACHTWOORDEN. Op het echte toestel zit er een pincode voor het manager-
 * en servicemenu (scherm "Beveiligd item" met een cijferpaneel). Die staat hier
 * net zomin in als bij de V400M — zie types.ts.
 *
 * VOORBEELDGEGEVENS. De opname komt van een echte klantlocatie: naam, adres,
 * telefoonnummer, TMS-ID, serienummer, IP-adressen en C-tap-nummers stonden er
 * gewoon in. Dat is allemaal vervangen. Wat blijft staan zijn de
 * SOFTWAREVERSIES en de structuur, want dat is waar de emulator over gaat.
 *
 * NIET ALLES IS VASTGELEGD. De app liep tijdens de inventarisatie vast op
 * "Zoeken naar terminals", en een aantal takken is bewust niet geopend omdat
 * ze iets veranderen of verbinding maken. Die schermen staan er wél in, met
 * `todo` en de reden erbij: "hier zijn we niet geweest" is bruikbare
 * informatie, "dit menu bestaat niet" zou een leugen zijn.
 * ────────────────────────────────────────────────────────────────────────
 */

/* SCHRIJFWIJZE. Labels en titels staan hier in gewone schrijfwijze en niet in
   kapitalen, precies zoals de Android-app ze toont ("Display info", niet
   "DISPLAY INFO"). Dat is geen kosmetiek: het is het eerste waaraan je ziet dat
   je naar de PAX-app kijkt en niet naar een Verifone. De labels van
   INFOSCHERMEN blijven wél in kapitalen — die toont de app zo. */

/** Een scherm dat we niet konden of wilden openen, met de reden erbij. */
const nietBezocht = (title: string, reden: string) =>
  ({ type: 'msg', title, lines: [reden], todo: true }) as const;

/** De vijf tests onder Comm test ▸ Terminal, allemaal eindpunten die meteen
 *  een verbinding opzetten — dus niet uitgevoerd. */
const commTest = (title: string) =>
  nietBezocht(title, 'NIET UITGEVOERD — ZET METEEN EEN VERBINDING OP');

const RUW = {
  /* ── opstartscherm ───────────────────────────────────────────────────── */
  start: { type: 'splash', title: '', ms: 1800, next: 'app' },

  /* ── startscherm van de APP (zit boven het hoofdmenu) ────────────────── */
  app: {
    type: 'menu',
    title: 'CCV Service app',
    items: [
      { label: 'Terminal', to: 'app_terminal' },
      { label: 'Menu', to: 'main' },
      { label: 'Pos Test', to: 'postest' },
      { label: 'Herdruk ticket', to: 'herdruk' },
    ],
    // Onderaan dit scherm staat het CCV-logo met een Privacy-link.
    voet: true,
  },
  app_terminal: nietBezocht('Terminal', 'NIET GEOPEND — START VERMOEDELIJK EEN TRANSACTIE'),
  postest: {
    type: 'menu',
    title: 'Pos Test',
    items: [
      { label: '€ 0,01', to: 'postest_bedrag' },
      { label: '€ 0,10', to: 'postest_bedrag' },
      { label: '€ 1,00', to: 'postest_bedrag' },
      { label: 'Aangepaste invoer', to: 'postest_invoer' },
    ],
  },
  postest_bedrag: { type: 'msg', title: 'Pos Test', lines: ['TOT 0,01 EUR', 'UW KAART AUB'] },
  postest_invoer: nietBezocht('Aangepaste invoer', 'VRAAGT OM EEN BEDRAG — NIET GEOPEND'),
  herdruk: { type: 'msg', title: 'Fout', lines: ['FAILURE'] },

  /* ── hoofdmenu ───────────────────────────────────────────────────────── */
  main: {
    type: 'menu',
    title: 'Hoofd menu',
    items: [
      { label: 'Display info', to: 'displayinfo' },
      { label: 'Manager menu', to: 'managermenu' },
      { label: 'Service menu', to: 'servicemenu' },
    ],
  },

  /* ── DISPLAY INFO ────────────────────────────────────────────────────── */
  displayinfo: {
    type: 'menu',
    title: 'Display info',
    items: [
      { label: 'Customer service', to: 'di_cs' },
      { label: 'Terminal', to: 'di_term' },
      { label: 'Ethernet', to: 'ethernet' },
      { label: 'Opi instellingen', to: 'di_opi' },
      { label: 'Hw info', to: 'di_hw' },
      { label: 'C-tap', to: 'di_ctap' },
      { label: 'Flexo', to: 'di_flexo' },
    ],
  },
  di_cs: { type: 'info', title: 'Customer service', rows: [['TELEFOONNUMMER:', '088 - 000 00 00']] },
  di_term: {
    type: 'info',
    title: 'Terminal',
    rows: [
      ['TMS TID:', '000000000'],
      ['CONF:', 'PAYGEAR'],
      ['CONF VERS:', '15.3.03.00'],
      ['SW VERS:', '15.3.03.30296'],
      ['PP VERS:', 'v0017.00.09+0'],
      ['', '11/09/2026  12:17:23'],
    ],
  },
  di_opi: {
    type: 'info',
    title: 'Opi',
    rows: [['POS MODE', 'TCP STANDAARD'], ['ADMIN MODE', 'TCP STANDAARD']],
  },
  di_hw: { type: 'info', title: 'Hw info', rows: [['S/N:', '0000000000']] },
  di_ctap: {
    type: 'info',
    title: 'C-tap',
    rows: [['Worldline EU:', '00000000'], ['CCVPay:', 'CT000000']],
  },
  /* Zes keer aangetikt, ook na een verse herstart van de app: geen scherm,
     geen melding. Het is niet de Flexo-module — onder MANAGER ▸ Rapporten ▸
     Flexo werkt het wel. Het ligt aan dit ene item. */
  di_flexo: nietBezocht('Flexo', 'GEEN REACTIE OP DIT ITEM — FLEXO WERKT WEL ONDER MANAGER ▸ RAPPORTEN'),

  /* ── ETHERNET (waar op de V400M wifi en mobiel zitten) ───────────────── */
  ethernet: {
    type: 'menu',
    title: 'Ethernet',
    items: [
      { label: 'Ip info', to: 'eth_ip' },
      { label: 'Hw info', to: 'eth_hw' },
    ],
  },
  eth_ip: {
    type: 'info',
    title: 'Ip info',
    rows: [
      ['', 'DYNAMISCH IP'],
      ['IP:', '192.168.1.42'],
      ['MASK:', '255.255.255.0'],
      ['GTWY:', 'INVALID'],
      ['DNS1:', ''],
      ['DNS2:', ''],
    ],
  },
  eth_hw: { type: 'info', title: 'Hw info', rows: [['MAC', 'N/A']] },

  /* ── MANAGER MENU ────────────────────────────────────────────────────── */
  managermenu: {
    type: 'menu',
    title: 'Manager menu',
    items: [
      { label: 'Rapporten', to: 'mgr_rap' },
      { label: 'Functies', to: 'mgr_fun' },
      { label: 'Configureren', to: 'mgr_cfg' },
      { label: 'Diagnose', to: 'mgr_diag' },
    ],
  },

  mgr_rap: {
    type: 'menu',
    title: 'Rapporten',
    items: [
      { label: 'Terminal', to: 'mgr_rap_term' },
      { label: 'C-tap', to: 'mgr_rap_ctap' },
      { label: 'Flexo', to: 'mgr_rap_flexo' },
    ],
  },
  mgr_rap_ctap: {
    type: 'menu',
    title: 'C-tap',
    items: [
      { label: 'Financieel', to: 'ctap_rap' },
      { label: 'Parameters', to: 'ctap_rap' },
      { label: 'Status', to: 'ctap_rap' },
      { label: 'Sessies', to: 'ctap_rap' },
    ],
  },
  ctap_rap: nietBezocht('C-tap', 'RAPPORT NIET UITGEDRAAID'),
  /* Flexo wérkt hier, terwijl het gelijknamige item onder DISPLAY INFO nergens
     op reageert. Het ligt dus aan dat ene item en niet aan een ontbrekende
     Flexo-module. */
  mgr_rap_flexo: {
    type: 'menu',
    title: 'Flexo',
    items: [{ label: 'Parameters', to: 'flexo_par' }],
  },
  flexo_par: {
    type: 'menu',
    title: 'Flexo',
    items: [{ label: 'Print services', to: 'flexo_print' }],
  },
  flexo_print: nietBezocht('Print services', 'NIET AANGETIKT — PRINT DIRECT'),
  mgr_rap_term: {
    type: 'menu',
    title: 'Terminal',
    items: [
      { label: 'Financieel', to: 'mgr_rap_fin' },
      { label: 'Parameters', to: 'mgr_rap_par' },
    ],
  },
  mgr_rap_fin: {
    type: 'menu',
    title: 'Financieel',
    items: [
      { label: 'Huidige periode', to: 'fin_periode' },
      // Periode 001 is nagelopen en heeft exact dezelfde twee keuzes; 000 is
      // daarom overgeslagen en wijst naar hetzelfde scherm.
      { label: 'Periode 001', to: 'fin_periode' },
      { label: 'Periode 000', to: 'fin_periode' },
    ],
  },
  fin_periode: {
    type: 'menu',
    title: 'Huidige periode',
    items: [
      { label: 'Algemeen', to: 'fin_alg' },
      { label: 'Per acquirer', to: 'fin_acq' },
    ],
  },
  /* Rapporten komen op dit toestel als BON-TEKST op het scherm, in een venster
     met de titel "Ticket". Dat is een van de duidelijkste verschillen met de
     V400M, waar het rapport naar de printer gaat. */
  fin_alg: {
    type: 'ticket',
    title: 'Ticket',
    regels: [
      'Voorbeeldzaak',
      '',
      'DATUM:      11/09/2026 12:47:05',
      'TOTALEN RAPPORT',
      'TUSSENTOTALEN',
      'TMS TERM-ID: 000000000',
      'PERIODE NR: 1',
      'PERIODE START: 11/09/2026 12:23:49',
      'PERIODE EINDE:       NOG OPEN',
      '====================',
      'GEEN TOTALEN BESCHIKBAAR',
      '====================',
      'EINDE RAPPORT',
    ],
  },
  fin_acq: {
    type: 'ticket',
    title: 'Ticket',
    regels: ['HUIDIGE PERIODE', 'ALGEMEEN', 'PER ACQUIRER'],
  },
  mgr_rap_par: {
    type: 'menu',
    title: 'Parameters',
    items: [
      { label: 'Algemeen', to: 'par_alg' },
      { label: 'Batterij', to: 'par_batt' },
    ],
  },
  par_batt: nietBezocht('Batterij', 'GAF GEEN LEESBAAR SCHERM IN DE OPNAME'),
  par_alg: {
    type: 'ticket',
    title: 'Ticket',
    regels: [
      'Voorbeeldzaak',
      'Voorbeeldstraat 1, Amsterdam',
      '000-0000000',
      '',
      'TERMINAL PARAMETER RAPPORT',
      'DATUM:      11/09/2026 12:54:19',
      'TIJDZONE: CET',
      'TMS TERM-ID: 000000000',
      'TMS-ID: 08900000000',
      'CONFIGURATIE NAAM: PAYGEAR',
      'CONFIGURATIE VERSIE: 15.3.03.00',
      'TERMINAL SOFTWARE VERSIE:',
      '  15.3.03.30296',
      'SOFTWARE BUILD:',
      '  2026-01-14 13:39:17',
      'SERIE NUMMER: 0000000000',
      'SECURITY MODULE VERSIE:',
      '  v0017.00.09+0288',
      'MODEM TYPE: ETHERNET',
      'IP CONFIGURATIE: DYNAMISCH',
      'TERMINAL IP ADRES: 192.168.1.42',
      'SUBNET MASK: 255.255.255.0',
      'GATEWAY: INVALID',
      'IP ADRES DNS1:',
      'IP ADRES DNS2:',
    ],
  },

  mgr_fun: {
    type: 'menu',
    title: 'Functies',
    items: [
      { label: 'Terminal', to: 'mgr_fun_term' },
      { label: 'C-tap', to: 'mgr_fun_ctap' },
    ],
  },
  /* "DEACTIVEER TERMINAL" en niet "ACTIVEER": het toestel in de opname was
     actief, en de app toont de omgekeerde actie. Ook geen DAGOPENING maar
     DAGAFSLUITING, en geen UITZETTEN. */
  mgr_fun_term: {
    type: 'menu',
    title: 'Terminal',
    items: [
      { label: 'Deactiveer terminal', to: 'fun_deact' },
      { label: 'Dagafsluiting', to: 'fun_dag' },
      { label: 'Contact tms', to: 'fun_tms' },
      { label: 'Cont. tms vannacht', to: 'fun_tmsnacht' },
      { label: 'Verstuur logging', to: 'fun_log' },
      { label: 'Opnieuw starten', to: 'fun_reboot' },
    ],
  },
  fun_deact: nietBezocht('Deactiveer terminal', 'NIET UITGEVOERD — SCHAKELT DE TERMINAL UIT'),
  fun_dag: nietBezocht('Dagafsluiting', 'NIET UITGEVOERD — SLUIT DE PERIODE AF'),
  fun_tms: nietBezocht('Contact tms', 'NIET UITGEVOERD — START EEN TMS-SESSIE'),
  fun_tmsnacht: nietBezocht('Cont. tms vannacht', 'NIET UITGEVOERD — PLANT EEN TMS-SESSIE'),
  fun_log: nietBezocht('Verstuur logging', 'NIET UITGEVOERD — VERSTUURT GEGEVENS'),
  fun_reboot: nietBezocht('Opnieuw starten', 'NIET UITGEVOERD — HERSTART DE TERMINAL'),
  mgr_fun_ctap: {
    type: 'menu',
    title: 'C-tap',
    items: [
      { label: 'Initialisatie', to: 'ctap_init' },
      { label: 'Contact acquirer', to: 'ctap_contact' },
    ],
  },
  ctap_init: nietBezocht('Initialisatie', 'NIET UITGEVOERD — WIJZIGT DE ACQUIRERCONFIGURATIE'),
  ctap_contact: nietBezocht('Contact acquirer', 'NIET UITGEVOERD — ZET EEN VERBINDING OP'),

  mgr_cfg: {
    type: 'menu',
    title: 'Configureren',
    items: [
      { label: 'Terminal', to: 'mgr_cfg_term' },
      { label: 'C-tap', to: 'cfg_ctap' },
    ],
  },
  /* Het menu-item heet "C-tap", het scherm dat opengaat heet "Configuratie". */
  cfg_ctap: {
    type: 'menu',
    title: 'Configuratie',
    items: [{ label: 'Aanpassen relaties', to: 'cfg_relaties' }],
  },
  cfg_relaties: nietBezocht('Aanpassen relaties', 'NIET AANGETIKT — WIJZIGT ACQUIRERRELATIES'),
  /* Vier items. Geen KEYPAD BL UIT en geen SCHERM HELDERHEID — die zitten wél
     op de V400M. */
  mgr_cfg_term: {
    type: 'menu',
    title: 'Terminal',
    items: [
      { label: 'Communicatie', to: 'cfg_comm' },
      { label: 'Manager wachtwoord', to: 'cfg_mgrpw' },
      { label: 'Medewerkers', to: 'cfg_medew' },
      { label: 'Taal', to: 'cfg_taal' },
    ],
  },
  /* Eén regel. Geen WIFI, geen MOBIELE NETWERKEN, geen USB GADGET — dit
     toestel hangt aan de kabel. */
  cfg_comm: { type: 'menu', title: 'Communicatie', items: [{ label: 'Ethernet', to: 'cfg_eth' }] },
  cfg_eth: {
    type: 'menu',
    title: 'Ethernet',
    items: [
      { label: 'Ip info', to: 'eth_ip' },
      { label: 'Ip instellingen', to: 'cfg_ipset' },
    ],
  },
  /* Eén knop, en precies de omgekeerde van wat je op de V400M ziet: die staat
     op vast IP en biedt "wijzig naar DHCP", dit toestel staat op DHCP. De losse
     velden voor IP, masker en gateway verschijnen vermoedelijk pas ná het
     omzetten. */
  cfg_ipset: {
    type: 'menu',
    title: 'Ip adressen',
    items: [{ label: 'Wijzig naar vast ip', to: 'cfg_vastip' }],
  },
  cfg_vastip: nietBezocht('Wijzig naar vast ip', 'NIET UITGEVOERD — WIJZIGT DE IP-INSTELLINGEN'),
  cfg_mgrpw: { type: 'msg', title: 'Manager wachtwoord', lines: ['HUIDIG WACHTWOORD', 'INVOEREN'] },
  cfg_medew: {
    type: 'menu',
    title: 'Medewerkers',
    items: [
      { label: 'Toevoegen', to: 'medew_actie' },
      { label: 'Verwijderen', to: 'medew_actie' },
      { label: 'Aanpassen', to: 'medew_actie' },
    ],
  },
  medew_actie: nietBezocht('Medewerkers', 'NIET GEOPEND — WIJZIGT GEGEVENS'),
  /* Een keuze werkt direct door: tikken op EN zet de hele app in het Engels
     (Communication / Merchant password / Attendants / Language). */
  cfg_taal: {
    type: 'menu',
    title: 'Taal',
    items: [
      { label: 'Nl', to: 'taal_gekozen' },
      { label: 'En', to: 'taal_gekozen' },
      { label: 'Fr', to: 'taal_gekozen' },
      { label: 'De', to: 'taal_gekozen' },
    ],
  },
  taal_gekozen: { type: 'msg', title: 'Taal', lines: ['TAAL DIRECT GEWIJZIGD', 'GEEN BEVESTIGING'] },

  /* ── DIAGNOSE onder MANAGER ──────────────────────────────────────────── */
  mgr_diag: {
    type: 'menu',
    title: 'Diagnose',
    items: [
      { label: 'Comm test', to: 'comm_test' },
      { label: 'Chip diagnose', to: 'diag_chip' },
    ],
  },
  comm_test: {
    type: 'menu',
    title: 'Comm test',
    items: [
      { label: 'Terminal', to: 'comm_term' },
      { label: 'C-tap', to: 'comm_ctap' },
    ],
  },
  comm_term: {
    type: 'menu',
    title: 'Terminal',
    items: [
      { label: 'Alle verbindingen', to: 'ct_alle' },
      { label: 'Tms configuratie', to: 'ct_tmscfg' },
      { label: 'Tms config backup', to: 'ct_tmsbackup' },
      { label: 'Tms installatie', to: 'ct_tmsinst' },
      { label: 'Https server', to: 'ct_https' },
    ],
  },
  ct_alle: commTest('Alle verbindingen'),
  ct_tmscfg: commTest('Tms configuratie'),
  ct_tmsbackup: commTest('Tms config backup'),
  ct_tmsinst: commTest('Tms installatie'),
  ct_https: commTest('Https server'),
  comm_ctap: {
    type: 'menu',
    title: 'C-tap',
    items: [
      { label: 'Acquirer init.', to: 'ct_acqinit' },
      { label: 'Acquirer auto.', to: 'ct_acqauto' },
      { label: 'Tsc schema', to: 'ct_tsc' },
    ],
  },
  ct_acqinit: nietBezocht('Acquirer init.', 'NIET UITGEVOERD — WIJZIGT DE ACQUIRERCONFIGURATIE'),
  ct_acqauto: nietBezocht('Acquirer auto.', 'NIET UITGEVOERD — WIJZIGT DE ACQUIRERCONFIGURATIE'),
  ct_tsc: nietBezocht('Tsc schema', 'NIET GEOPEND'),
  /* LET OP. Dit scherm blokkeert de terugknop, óók de ingebouwde pijl van
     Android. De app moest via de recents-lijst worden afgesloten en gaf daarna
     "Failed to connect to the terminal". Dat is precies het soort ding waarvoor
     deze emulator bestaat: je wilt dit wéten voordat je het bij een klant doet. */
  diag_chip: {
    type: 'msg',
    title: 'Chip insteken',
    lines: ['WACHT OP EEN KAART', '', 'LET OP: DE TERUGKNOP', 'WERKT HIER NIET'],
  },

  /* ── SERVICE MENU ────────────────────────────────────────────────────
     Let op: dit is een ánder menu dan onder MANAGER, niet een kopie met een
     paar extra's. Rapporten, Functies en Configureren hebben er alle drie
     andere items onder. */
  servicemenu: {
    type: 'menu',
    title: 'Service menu',
    items: [
      { label: 'Rapporten', to: 'svc_rap' },
      { label: 'Functies', to: 'svc_fun' },
      { label: 'Configureren', to: 'svc_cfg' },
      { label: 'Diagnose', to: 'svc_diag' },
      { label: 'Manager menu', to: 'managermenu' },
    ],
  },

  /* ── SERVICE ▸ RAPPORTEN ─────────────────────────────────────────────── */
  svc_rap: {
    type: 'menu',
    title: 'Rapporten',
    items: [
      { label: 'Terminal', to: 'svc_rap_term' },
      { label: 'C-tap', to: 'svc_rap_ctap' },
      { label: 'Flexo', to: 'svc_rap_flexo' },
    ],
  },
  svc_rap_ctap: nietBezocht('C-tap', 'NIET GEOPEND'),
  svc_rap_flexo: nietBezocht('Flexo', 'NIET GEOPEND'),
  /* Heel andere items dan MANAGER ▸ Rapporten ▸ Terminal, waar Financieel en
     Parameters staan. */
  svc_rap_term: {
    type: 'menu',
    title: 'Terminal',
    items: [
      { label: 'Parameters', to: 'svc_rap_par' },
      { label: 'Management info', to: 'svc_rap_mgmt' },
      { label: 'Key', to: 'svc_rap_key' },
      { label: 'Tms sessies', to: 'svc_rap_tms' },
      { label: 'Laatste fout ber.', to: 'svc_rap_fout' },
      { label: 'Crash rapport', to: 'svc_rap_crash' },
    ],
  },
  svc_rap_mgmt: nietBezocht('Management info', 'NIET GEOPEND'),
  svc_rap_key: nietBezocht('Key', 'NIET GEOPEND'),
  /* Het enige scherm in beide opnames dat écht scrollt: 23 regels, ongeveer
     acht tegelijk in beeld, bedienen met een veeg — geen bladerpijlen. Na
     Algemeen en Contactloos volgen losse parameterbestanden.

     De xml-namen zijn per regel van het scherm afgelezen; een paar korte namen
     stonden klein in beeld en kunnen een teken schelen. */
  svc_rap_par: {
    type: 'menu',
    title: 'Parameters',
    items: [
      { label: 'Algemeen', to: 'svc_rap_par_item' },
      { label: 'Contactloos', to: 'svc_rap_par_item' },
      { label: 'Ctp11cbt.xml', to: 'svc_rap_par_item' },
      { label: 'Ctp12cbt.xml', to: 'svc_rap_par_item' },
      { label: 'Ctp11apg.xml', to: 'svc_rap_par_item' },
      { label: 'Ctp12apg.xml', to: 'svc_rap_par_item' },
      { label: 'Srv01tcd.xml', to: 'svc_rap_par_item' },
      { label: 'Srv01els.xml', to: 'svc_rap_par_item' },
      { label: 'Srv01mqt.xml', to: 'svc_rap_par_item' },
      { label: 'Srv01sig.xml', to: 'svc_rap_par_item' },
      { label: 'Srv01myc.xml', to: 'svc_rap_par_item' },
      { label: 'Flx01ap.xml', to: 'svc_rap_par_item' },
      { label: 'Srv01ct.xml', to: 'svc_rap_par_item' },
      { label: 'Rkl_descr.xml', to: 'svc_rap_par_item' },
      { label: 'Mpa01ap.xml', to: 'svc_rap_par_item' },
      { label: 'Srv01prf.xml', to: 'svc_rap_par_item' },
      { label: 'Ctp01cup.xml', to: 'svc_rap_par_item' },
      { label: 'Ctpcbpt.xml', to: 'svc_rap_par_item' },
      { label: 'Ctp01tmg.xml', to: 'svc_rap_par_item' },
      { label: 'Ctpnpwlt.xml', to: 'svc_rap_par_item' },
      { label: 'Ctp01tg.xml', to: 'svc_rap_par_item' },
      { label: 'Srv00bp.xml', to: 'svc_rap_par_item' },
      { label: 'Ctpcblt.xml', to: 'svc_rap_par_item' },
    ],
  },
  svc_rap_par_item: nietBezocht('Parameters', 'RAPPORT NIET UITGEDRAAID'),
  /* Deze tussenlaag stond niet in de eerdere aanname. */
  svc_rap_tms: {
    type: 'menu',
    title: 'Tms sessies',
    items: [
      { label: 'Laatste', to: 'svc_tms_laatste' },
      { label: 'Laatste gelukt', to: 'svc_tms_leeg' },
      { label: 'Laatste download', to: 'svc_tms_leeg' },
    ],
  },
  svc_tms_laatste: {
    type: 'ticket',
    title: 'Ticket',
    regels: [
      '** CONFIGURATIE **',
      '** GESLAAGD **',
      '11.09.2026',
      '12:09:30',
      '',
      'TMS TERM-ID:',
      '000000000',
      'TERMINAL SOFTWARE VERSIE:',
      '15.3.03.3029',
      'TERMINAL TAAL:',
      'NL',
      'CONFIGURATIE NAAM:',
      'PAYGEAR',
      'CONFIGURATIE VERSIE:',
      '15.3.03.00',
      'TMS-ID:',
      '08900000000',
      'TMS CONFIG DATUM:',
      '18/09/2026',
      'TMS CONFIG TIJD:',
      '00:35:34',
      'TMS SESSIE TYPE:',
      'CONFIGURATIECONTROLE',
      'TMS CONFIG INTERVAL:',
      '7',
      'HELPDESK TEL NR:',
      '088 000 00 00',
      'EINDE RAPPORT',
    ],
  },
  /* Zo ziet "geen gegevens" eruit: een Ticket met alleen het menupad in
     hoofdletters. Dit verklaart de lege grijze schermen uit de eerste opname —
     het is geen storing en geen half geladen scherm. */
  svc_tms_leeg: {
    type: 'ticket',
    title: 'Ticket',
    regels: ['TMS SESSIES', 'LAATSTE', 'LAATSTE GELUKT', 'LAATSTE DOWNLOAD'],
  },
  svc_rap_fout: nietBezocht('Laatste fout ber.', 'GEEN SCHERM EN GEEN MELDING — ER IS NIETS TE TONEN'),
  svc_rap_crash: nietBezocht('Crash rapport', 'GEEN SCHERM EN GEEN MELDING — ER IS NIETS TE TONEN'),

  /* ── SERVICE ▸ FUNCTIES ──────────────────────────────────────────────── */
  svc_fun: {
    type: 'menu',
    title: 'Functies',
    items: [
      { label: 'Terminal', to: 'svc_fun_term' },
      { label: 'C-tap', to: 'svc_fun_ctap' },
    ],
  },
  /* HET ITEM HEET "Terminal", HET SCHERM "Contact tms". Er zit geen tussenmenu
     tussen — nagemeten met de terugknop: één tik brengt je terug op Functies.
     Deactiveer terminal, Dagafsluiting, Cont. tms vannacht, Verstuur logging en
     Opnieuw starten staan hier dus NIET; die zitten alleen onder MANAGER. */
  svc_fun_term: {
    type: 'menu',
    title: 'Contact tms',
    items: [
      { label: 'Normaal', to: 'svc_tms_start' },
      { label: 'Noodoproep', to: 'svc_tms_start' },
      { label: 'Hw installatie', to: 'svc_tms_start' },
    ],
  },
  svc_tms_start: nietBezocht('Contact tms', 'NIET UITGEVOERD — START EEN TMS-SESSIE'),
  /* Vier items tegen twee onder MANAGER: geen Initialisatie, wel twee
     log-acties. */
  svc_fun_ctap: {
    type: 'menu',
    title: 'C-tap',
    items: [
      { label: 'Contact acquirer', to: 'svc_ctap_verbind' },
      { label: 'Logs opschonen', to: 'svc_ctap_logs' },
      { label: 'Verwijder logs', to: 'svc_ctap_logs' },
      { label: 'Laad tsc schema', to: 'svc_ctap_tsc' },
    ],
  },
  svc_ctap_verbind: nietBezocht('Contact acquirer', 'NIET UITGEVOERD — ZET EEN VERBINDING OP'),
  svc_ctap_logs: nietBezocht('Logs', 'NIET UITGEVOERD — WIST GEGEVENS'),
  svc_ctap_tsc: nietBezocht('Laad tsc schema', 'NIET UITGEVOERD — WIJZIGT DE CONFIGURATIE'),

  /* ── SERVICE ▸ CONFIGUREREN ──────────────────────────────────────────── */
  /* Eén item; onder MANAGER staat hier ook C-tap. */
  svc_cfg: { type: 'menu', title: 'Configureren', items: [{ label: 'Terminal', to: 'svc_cfg_term' }] },
  /* Zes items, lijst is compleet (veegpoging scrollt niet). Geen Communicatie,
     geen Scherm kalibreren, geen Keypad bl uit — die drie staan wél op de
     Verifone. */
  svc_cfg_term: {
    type: 'menu',
    title: 'Terminal',
    items: [
      { label: 'Opi instellingen', to: 'svc_opi' },
      { label: 'Datum / tijd', to: 'svc_datum' },
      { label: 'Ils2com', to: 'svc_ils' },
      { label: 'Reset manager ww', to: 'svc_reset' },
      { label: 'Reset transactie ww', to: 'svc_reset' },
      { label: 'Laden pp keys', to: 'svc_ppkeys' },
    ],
  },
  svc_reset: nietBezocht('Reset wachtwoord', 'NIET GEOPEND — HET OPENEN KAN DE ACTIE ZELF AL ZIJN'),
  svc_ppkeys: nietBezocht('Laden pp keys', 'NIET GEOPEND — HET OPENEN KAN DE ACTIE ZELF AL ZIJN'),
  /* De schrijfbare tegenhanger van het alleen-lezen scherm "Opi" onder
     DISPLAY INFO. */
  svc_opi: {
    type: 'menu',
    title: 'Opi instellingen',
    items: [
      { label: 'Pos mode', to: 'svc_opi_mode' },
      { label: 'Admin mode', to: 'svc_opi_mode' },
      { label: 'Configureer poorten', to: 'svc_opi_poorten' },
    ],
  },
  /* Pos mode en Admin mode tonen dezelfde drie keuzes. Het scherm markeert NIET
     welke er actief is; die lees je af via DISPLAY INFO ▸ Opi instellingen. */
  svc_opi_mode: {
    type: 'menu',
    title: 'Pos mode',
    items: [
      { label: 'Tcp standaard', to: 'svc_opi_keuze' },
      { label: 'Tcp flat-incoming', to: 'svc_opi_keuze' },
      { label: 'Serieel', to: 'svc_opi_keuze' },
    ],
  },
  svc_opi_keuze: nietBezocht('Opi mode', 'NIET AANGETIKT — DE KEUZE GAAT DIRECT IN'),
  svc_opi_poorten: {
    type: 'menu',
    title: 'Configureer poorten',
    items: [
      { label: 'Pos listen poort', to: 'poort_poslisten' },
      { label: 'Pos device poort', to: 'poort_posdevice' },
      { label: 'Admin listen poort', to: 'poort_adminlisten' },
      { label: 'Admin device poort', to: 'poort_admindevice' },
    ],
  },
  /* Deze vier schermen zijn INVOERvelden: een leeg veld met een cijferpaneel,
     en de huidige waarde erboven tussen haakjes. Hier staat alleen die huidige
     waarde — het toetsenpaneel zit er bewust niet in, net als bij de
     wachtwoordschermen. */
  poort_poslisten: {
    type: 'info',
    title: 'Pos listen poort',
    rows: [['HUIDIGE WAARDE:', '4100'], ['', 'NIEUWE WAARDE INVOEREN']],
  },
  poort_posdevice: {
    type: 'info',
    title: 'Pos device poort',
    rows: [['HUIDIGE WAARDE:', '4102'], ['', 'NIEUWE WAARDE INVOEREN']],
  },
  poort_adminlisten: {
    type: 'info',
    title: 'Admin listen poort',
    rows: [['HUIDIGE WAARDE:', '4101'], ['', 'NIEUWE WAARDE INVOEREN']],
  },
  poort_admindevice: {
    type: 'info',
    title: 'Admin device poort',
    rows: [['HUIDIGE WAARDE:', '4103'], ['', 'NIEUWE WAARDE INVOEREN']],
  },
  /* Het menu-item heet "Datum / tijd", maar het eerste scherm vraagt alleen de
     datum; de tijd volgt vermoedelijk na OK. Niet doorgeklikt. */
  svc_datum: {
    type: 'info',
    title: 'Datum (jjjjmmdd)',
    rows: [['HUIDIGE WAARDE:', '20260911'], ['', 'NIEUWE DATUM INVOEREN']],
  },
  /* Leeg tekstveld met alleen een OK-knop: geen label, geen huidige waarde en
     geen cijferpaneel. Verwacht dus vrije tekst via een extern toetsenbord. */
  svc_ils: { type: 'msg', title: 'Ils2com', lines: ['LEEG TEKSTVELD', 'VRIJE INVOER'] },

  /* ── SERVICE ▸ DIAGNOSE ──────────────────────────────────────────────── */
  /* Duidelijk uitgebreider dan Diagnose onder MANAGER MENU. */
  svc_diag: {
    type: 'menu',
    title: 'Diagnose',
    items: [
      { label: 'Magstr lezer test', to: 'svc_msr' },
      { label: 'Printer test', to: 'svc_print' },
      { label: 'Comm test', to: 'comm_test' },
      { label: 'Batterij test', to: 'svc_batt' },
      { label: 'Pinpad menu', to: 'svc_pinpad' },
    ],
  },
  svc_msr: {
    type: 'msg',
    title: 'Kaarthdr msr gereed',
    lines: ['KAART DOORHALEN', '', 'LET OP: DE TERUGKNOP', 'WERKT HIER NIET'],
  },
  svc_print: nietBezocht('Printer test', 'GEEN SCHERMWIJZIGING — PRINT VERMOEDELIJK DIRECT EEN TESTBON'),
  svc_batt: {
    type: 'info',
    title: 'Batterij test',
    rows: [
      ['VOEDING:', 'EXTERN'],
      ['BATTERIJ NIVEAU:', 'OK'],
      ['CAPACITEIT(AH):', '0.820'],
      ['CAP. MAX (AH):', '1.000'],
      ['VOLTAGE (V):', '4.199'],
    ],
  },
  svc_pinpad: {
    type: 'msg',
    title: 'Pinpad menu',
    lines: ['GOOIT JE TERUG NAAR', 'HET STARTSCHERM VAN DE APP'],
  },

} as const satisfies Record<string, Scherm>;

export type PaxSchermId = keyof typeof RUW;

/** Dezelfde compileertijd-controle als bij de V400M: elke `to` en elke `next`
 *  moet een scherm noemen dat hierboven bestaat. */
export const PAX_SCHERMEN: Readonly<Record<PaxSchermId, Scherm<PaxSchermId>>> = RUW;

export const PAX: Toestel = {
  slug: 'pax',
  aliassen: ['a77', 'a920', 'a35'],
  naam: 'PAX A77, A920 & A35',
  kort: 'PAX',
  merk: 'Android CCV Service app · 15.3.03',
  omschrijving:
    'De Android-versie van de CCV Service app: grote knoppen, alles op één scherm en een startscherm boven het hoofdmenu. Dezelfde menu’s als op een Verifone, een compleet ander beeld.',
  chrome: 'pax',
  schermen: PAX_SCHERMEN,
  wortel: 'app',
  splash: 'start',
  // Elk menu in de opname paste op één scherm; geen enkele lijst had pijlen.
  regelsPerScherm: null,
  beginstaat: { wifiAan: false, wifiVerbonden: false, ipModus: 'DYNAMISCH IP' },
  snel: [
    { label: 'Ethernet / IP', scherm: 'eth_ip' },
    { label: 'Diagnose', scherm: 'svc_diag' },
    { label: 'OPI-poorten', scherm: 'svc_opi_poorten' },
    { label: 'TMS-sessies', scherm: 'svc_rap_tms' },
  ],
  bron:
    'Opgenomen op een PAX A77 met de CCV Service app (softwareversie 15.3.03.30296), in twee sessies. A920 en A35 draaien dezelfde app. Takken die niet geopend zijn — omdat ze iets wijzigen, een verbinding opzetten of gegevens wissen — staan als zodanig gemarkeerd. Bedrijfsnaam, adres, telefoonnummer, TMS-ID, serienummer, IP-adressen en C-tap-nummers zijn verzonnen voorbeelden; softwareversies en poortnummers zijn echt.',
  serviceScherm: 'servicemenu',
  managerScherm: 'managermenu',
  managerCode: '99999',
};

/* Houdt de compiler eraan dat elk menu-item van het juiste type is; puur een
   controle, verder ongebruikt. */
export type PaxRegel = MenuRegel<PaxSchermId>;
